<template>
  <div
    ref="rootEl"
    class="claw-rail"
    :class="{ 'claw-rail--mounted': mountedCount > 0 }"
    @mousemove="onPointerMove"
    @mouseleave="hoveredPos = null"
    @click="onClick"
  >
    <!-- 刻度短横杠 -->
    <span
      v-for="(tick, i) in layout"
      :key="nodes[i].key + '-tick'"
      class="claw-rail__tick"
      :style="tickStyle(i, tick.top)"
      @click.stop="onTickClick(i)"
    ></span>

    <!-- 悬浮预览（hover 某个刻度时显示） -->
    <div
      v-if="hoveredNode && hoveredPos != null && layout[hoveredPos]"
      class="claw-rail__preview"
      :style="previewStyle(layout[hoveredPos].top)"
    >
      <p class="claw-rail__preview-q">{{ hoveredNode.question || '无摘要' }}</p>
      <p
        v-if="hoveredNode.answer"
        class="claw-rail__preview-a"
      >{{ hoveredNode.answer }}</p>
      <p v-else class="claw-rail__preview-empty">（暂无回答）</p>
    </div>
  </div>
</template>

<script>
/**
 * 消息导航刻度尺 —— 移植自客户端 apps/electron/src/renderer/components/chat/MessageRail.tsx
 *
 * 关键数值严格对齐客户端：
 *   RAIL_WIDTH   = 28   刻度尺总宽
 *   TICK_RIGHT   = 2    刻度离 rail 右边的距离
 *   TICK_HEIGHT  = 2    刻度短横杠高度
 *   TICK_RADIUS  = 0.5  圆角（基本方的）
 *   TIER1_LEN    = 20   active（当前视口内）时的长度
 *   TIER2_LEN    = 17   hover 时的长度
 *   TIER3_LEN    = 12   其余时的长度
 *   RAIL_PAD     = 6    首尾刻度距 rail 上下边界的 padding
 *   TICK_STEP    = 12   刻度间的最大间距（超过则均匀压缩）
 *   PREVIEW_W    = 320  悬浮预览卡宽度
 *   PREVIEW_GAP  = 8    预览卡和 rail 之间的间距
 *   PREVIEW_SAFE = 60   预览卡 top 的安全边界（避免溢出 rail 上下）
 */
const RAIL_WIDTH = 28
const TICK_RIGHT = 2
const TICK_HEIGHT = 2
const TICK_RADIUS = 0.5
const TIER1_LEN = 20
const TIER2_LEN = 17
const TIER3_LEN = 12
const RAIL_PAD = 6
const TICK_STEP = 12
const PREVIEW_W = 320
const PREVIEW_GAP = 8
const PREVIEW_SAFE = 60

// 主题色映射：客户端用 color-mix + CSS 变量，这里用 SCSS 变量/硬编码
const COLOR = {
  tier1: '#1f2329', // active —— 深灰
  tier2: '#646a73', // hover —— 悬浮态（比 active 浅但明显）
  tier3: '#9a9ea6', // rest —— 普通态（之前 #c6c8cf 太浅看不清）
  unmounted: '#d9dbe0' // 还没渲染到视口的刻度（mounted 后会变成 tier3）
}

function computeLayout(count, height) {
  if (height <= 0 || count <= 0) return { ys: [], spacing: 0 }
  if (count === 1) return { ys: [height / 2], spacing: TICK_STEP }
  const usable = Math.max(1, height - 2 * RAIL_PAD)
  const fullSpacing = usable / (count - 1)
  const spacing = Math.min(TICK_STEP, fullSpacing)
  const start = spacing < TICK_STEP
    ? RAIL_PAD
    : height / 2 - ((count - 1) * TICK_STEP) / 2
  const ys = []
  for (let i = 0; i < count; i++) {
    ys.push(start + i * spacing)
  }
  return { ys, spacing }
}

export default {
  name: 'CosmoClawMessageRail',
  props: {
    // 归并后的 turn 列表（来自 buildTurns）
    nodes: { type: Array, default: () => [] },
    // 当前视口对应的 turn index（由父组件滚动联动算出）
    activeIndex: { type: Number, default: null },
    // 当前已挂载（已滚动到过 / IntersectionObserver 触发过）的 turn key 集合
    mountedKeys: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      height: 0,
      hoveredPos: null,
      roTimer: null
    }
  },
  computed: {
    layout() {
      const { ys, spacing } = computeLayout(this.nodes.length, this.height)
      return ys.map(top => ({ top, spacing }))
    },
    hoveredNode() {
      return this.hoveredPos != null ? this.nodes[this.hoveredPos] : null
    },
    mountedCount() {
      return this.nodes.filter(n => this.mountedKeys && this.mountedKeys[n.key]).length
    }
  },
  watch: {
    nodes() {
      this.$nextTick(this.recalcHeight)
    }
  },
  mounted() {
    this.recalcHeight()
    // 监听 window resize + 消息容器滚动时重新算高度（用 throttle 避免抖动）
    this._resizeHandler = () => this.recalcHeight()
    window.addEventListener('resize', this._resizeHandler)
    // 父容器高度变化（消息列表增长）也会影响 layout，交给外部在 messages 变更时 call 本组件的 recalcHeight
  },
  beforeDestroy() {
    window.removeEventListener('resize', this._resizeHandler)
    if (this.roTimer) clearTimeout(this.roTimer)
  },
  methods: {
    recalcHeight() {
      const el = this.$refs.rootEl
      if (!el) return
      const h = el.clientHeight
      if (h !== this.height) this.height = h
    },

    onPointerMove(e) {
      const el = this.$refs.rootEl
      if (!el || this.layout.length === 0) return
      const rect = el.getBoundingClientRect()
      const relY = e.clientY - rect.top
      const { ys, spacing } = computeLayout(this.nodes.length, this.height)
      const half = Math.max(4, spacing / 2)
      if (relY < ys[0] - half || relY > ys[ys.length - 1] + half) {
        this.hoveredPos = null
        return
      }
      const raw = Math.round((relY - ys[0]) / spacing)
      this.hoveredPos = Math.max(0, Math.min(ys.length - 1, raw))
    },

    onClick() {
      if (this.hoveredPos != null && this.nodes[this.hoveredPos]) {
        this.$emit('select', this.hoveredPos)
      }
    },

    onTickClick(i) {
      if (this.nodes[i]) {
        this.$emit('select', i)
      }
    },

    tickStyle(i, top) {
      const node = this.nodes[i]
      if (!node) return {}
      const isActive = this.activeIndex === i
      const isHovered = this.hoveredPos === i
      const tier = isActive ? 1 : isHovered ? 2 : 3
      const width = isActive ? TIER1_LEN : isHovered ? TIER2_LEN : TIER3_LEN

      const mounted = this.mountedKeys && this.mountedKeys[node.key]
      // 颜色优先级：active > hover > 未挂载浅色 > 普通
      // active 是当前视口位置指示，必须始终可见，不能被 unmounted 浅色盖掉
      const color = tier === 1
        ? COLOR.tier1
        : tier === 2
          ? COLOR.tier2
          : !mounted
            ? COLOR.unmounted
            : COLOR.tier3

      return {
        top: 0,
        right: TICK_RIGHT + 'px',
        transform: `translateY(calc(${top}px - 50%))`,
        width: width + 'px',
        height: TICK_HEIGHT + 'px',
        borderRadius: TICK_RADIUS + 'px',
        backgroundColor: color
      }
    },

    previewStyle(top) {
      const half = PREVIEW_SAFE
      const safeTop = Math.min(Math.max(top, half), Math.max(half, this.height - half))
      return {
        top: safeTop + 'px',
        right: (RAIL_WIDTH + PREVIEW_GAP) + 'px',
        width: PREVIEW_W + 'px'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.claw-rail {
  position: relative;
  z-index: 20;
  width: 28px;
  height: 100%;
  flex-shrink: 0;
  user-select: none;
  cursor: pointer;
}

.claw-rail__tick {
  position: absolute;
  transition: width 0.15s ease-out, background-color 0.15s ease-out;
  will-change: transform;
}
/* 扩大刻度点击热区：只有 2px 高很难点中 */
.claw-rail__tick::after {
  content: '';
  position: absolute;
  top: -6px;
  bottom: -6px;
  left: -4px;
  right: -4px;
  cursor: pointer;
}

.claw-rail__preview {
  position: absolute;
  transform: translateY(-50%);
  pointer-events: none; // 不拦截鼠标，让 hover 保持在 rail 上
  z-index: 30;
}

.claw-rail__preview-q {
  margin: 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.38;
  color: #1f2329;
}

.claw-rail__preview-a {
  margin: 4px 0 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  font-size: 12px;
  line-height: 1.57;
  color: rgba(31, 35, 41, 0.85);
}

.claw-rail__preview-empty {
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.57;
  color: rgba(143, 149, 158, 0.5);
  font-style: italic;
}

/* 预览卡片整体外壳：border + 圆角 + 半透明背景 + 轻微阴影 + backdrop-blur */
.claw-rail__preview {
  padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
</style>
